import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { FormInput, FormTextarea, FormAsyncCombobox, FormCombobox } from '@/components/forms'
import { useTimezones, useCustomers, useSearchCustomers, useCustomersByIds } from '@/hooks/api'
import { usePermissions } from '@/hooks/api'
import { GooglePlacesAutocomplete } from '../GooglePlacesAutocomplete'
import { OpeningHoursSection } from '../OpeningHoursSection'
import { WhatsAppImageUploader } from '../WhatsAppImageUploader'

/**
 * About Tab - Business information and basic settings
 *
 * Includes:
 * - Name field
 * - Business details (about business)
 * - Customer ID selector (admin/partner only)
 * - Business timezone selector
 * - Google Places autocomplete for business location
 * - Opening hours from Google (when place is selected)
 * - WhatsApp profile image uploader with validation
 */
export function AboutTab() {
  const { t } = useTranslation()
  const { control, watch } = useFormContext()
  const { can } = usePermissions()

  const customerId = watch('customer_id')
  const isAdminOrPartner = can('edit:customers')

  // Customer search state
  const [customerSearchTerm, setCustomerSearchTerm] = useState('')

  // Fetch data
  const { data: timezones = [], isLoading: isLoadingTimezones } = useTimezones()
  const { data: searchedCustomers = [], isLoading: isSearchingCustomers } =
    useSearchCustomers(customerSearchTerm, isAdminOrPartner)
  const { data: selectedCustomers = [] } = useCustomersByIds(
    customerId ? [customerId] : [],
    isAdminOrPartner && !!customerId
  )

  // Build timezone options
  const timezoneOptions = useMemo(
    () =>
      timezones.map((tz) => ({
        value: tz,
        label: tz,
      })),
    [timezones]
  )

  // Build customer options
  const customerOptions = useMemo(
    () =>
      searchedCustomers.map((customer) => ({
        value: customer.id,
        label: customer.name,
      })),
    [searchedCustomers]
  )

  const selectedCustomerOptions = useMemo(
    () =>
      selectedCustomers.map((customer) => ({
        value: customer.id,
        label: customer.name,
      })),
    [selectedCustomers]
  )

  return (
    <div className="space-y-8">
      {/* Name Section */}
      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
        <div>
          <h3 className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">{t('resources.assistants.about.business_name')}</h3>
          <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
            {t('resources.assistants.about.name_description', { defaultValue: 'Enter the name of your business as it will be used by the assistant' })}
          </p>
        </div>
        <FormInput name="name" control={control} placeholder={t('resources.assistants.about.name_placeholder', { defaultValue: 'Enter business name' })} required />
      </section>

      {/* Business Details Section */}
      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
        <div>
          <h3 className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            {t('resources.assistants.about.business_description')}
          </h3>
          <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
            {t('resources.assistants.about.business_description_helper')}
          </p>
        </div>
        <FormTextarea
          name="prompt_scenarios.about_business"
          control={control}
          placeholder="e.g., Creative Mediterranean fare for breakfast, lunch & dinner is served in this chic, light-filled spot. Service options: Has outdoor seating..."
          autoResize
        />
      </section>

      {/* Customer ID Section (Admin/Partner only) */}
      {isAdminOrPartner && (
        <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
          <div>
            <h3 className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              {t('resources.assistants.about.customer')}
            </h3>
            <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
              {t('resources.assistants.about.customer_description', { defaultValue: 'The customer associated with this assistant' })}
            </p>
          </div>
          <FormAsyncCombobox
            name="customer_id"
            control={control}
            placeholder={t('resources.conversations.filters.search_customers')}
            options={customerOptions}
            selectedOptions={selectedCustomerOptions}
            onSearch={setCustomerSearchTerm}
            isLoading={isSearchingCustomers}
            isRequired
          />
        </section>
      )}

      {/* Business Timezone Section */}
      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
        <div>
          <h3 className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            {t('resources.assistants.about.timezone')}
          </h3>
          <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
            {t('resources.assistants.about.timezone_description', { defaultValue: 'Select the timezone your business operates in' })}
          </p>
        </div>
        <FormCombobox
          name="timezone"
          control={control}
          options={timezoneOptions}
          placeholder={t('resources.assistants.about.timezone_placeholder', { defaultValue: 'Search timezones...' })}
          isRequired
        />
      </section>

      {/* Google Place ID Section */}
      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
        <div className="space-y-6">
          <div>
            <h3 className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              {t('resources.assistants.about.location')}
            </h3>
            <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
              {t('resources.assistants.about.location_description', { defaultValue: 'Select your business location' })}
            </p>
            <GooglePlacesAutocomplete name="config.google_place_id" label="" description="" />
          </div>

          {/* Opening Hours (shown when Google Place is selected) */}
          <OpeningHoursSection />
        </div>
      </section>

      {/* WhatsApp Profile Image Section */}
      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
        <WhatsAppImageUploader />
      </section>
    </div>
  )
}

export default AboutTab
